import { useCallback, useEffect, useRef } from "react";
import { debounce } from "lodash";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { isValidURL } from "@/react/helpers/urlHelpers/isValidUrl";
import { useToast } from "@circle-react-shared/uikit/ToastV2";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Form } from "@circle-react-uikit/Form";
import { useCreateEmbed } from "./hooks/useCreateEmbed";

interface EmbedFieldProps {
  label: string;
  name: string;
}

interface EmbedData {
  html: string;
  url: string;
}

interface EmbedFieldFormData {
  [key: string]: EmbedData;
}

export const EmbedField = ({ name, label }: EmbedFieldProps) => {
  const toast = useToast();
  const createEmbed = useCreateEmbed();
  const { setValue, watch, setError, clearErrors } =
    useFormContext<EmbedFieldFormData>();
  const previousUrlRef = useRef(watch(`${name}.url`));

  const url = watch(`${name}.url`);

  const handleEmbed = useCallback(
    async (value: string) => {
      if (!value.trim()) {
        setValue(name, { url: "", html: "" }, { shouldDirty: true });
        return;
      }

      if (!isValidURL(value)) {
        setError(`${name}.url`, {
          type: "manual",
          message: t("builder_ui.embed.invalid_url"),
        });
        return;
      }

      clearErrors(`${name}.url`);

      try {
        const { oembed } = await createEmbed.mutateAsync(value);
        setValue(
          name,
          {
            url: oembed.url,
            html: oembed.html,
          },
          { shouldDirty: true, shouldValidate: true },
        );
      } catch {
        setError(`${name}.url`, {
          type: "manual",
          message: t("builder_ui.embed.invalid_embed_response"),
        });
        toast.error(t("builder_ui.embed.invalid_embed_response"));
      }
    },
    [name, setValue, setError, clearErrors, createEmbed, toast],
  );

  const debouncedFetchRef = useRef(
    debounce((value: string) => {
      void handleEmbed(value);
    }, 500),
  );

  useEffect(() => {
    if (url === previousUrlRef.current) {
      return;
    }

    previousUrlRef.current = url;
    const debouncedFn = debouncedFetchRef.current;

    debouncedFn(url);

    return () => {
      debouncedFn.cancel();
    };
  }, [url]);

  return (
    <div className="flex flex-col gap-2">
      <Form.Item
        name={`${name}.url`}
        label={label}
        hideDescription
        hideBorder
        labelClassName="!font-normal text-xs"
        isFirstChild
        errorClassName="!mb-0 mt-2 text-sm text-red-500"
      >
        <Form.Input
          name={`${name}.url`}
          type="url"
          aria-label={label}
          placeholder={t("builder_ui.embed.url_placeholder")}
          disabled={createEmbed.isLoading}
        />
        {createEmbed.isLoading && (
          <div className="mt-2">
            <Typography.LabelXs color="text-default">
              {t("builder_ui.embed.fetching_embed")}
            </Typography.LabelXs>
          </div>
        )}
      </Form.Item>

      <Typography.LabelXs color="text-dark">
        {t("builder_ui.embed.description.start")}
        <a
          href="https://iframely.com/domains"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-1 underline"
        >
          {t("builder_ui.embed.description.providers")}
        </a>
        {t("builder_ui.embed.description.end")}
      </Typography.LabelXs>
    </div>
  );
};

EmbedField.displayName = "EmbedField";
